import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Eye } from '@phosphor-icons/react';
import { TaskCard } from './TaskCard';
import type { Task, EffectiveCapacidad } from '@/types';
import { contrastingText } from '@/lib/utils';

interface Props {
  groupId: number;
  groupName: string;
  groupColor: string;
  tasks: Task[];
  // Solo se pasa en vista por responsable + sprint elegido. Si esta presente,
  // la columna muestra una barra de capacidad debajo del header con
  // horasAsignadas / horasEfectivas y color segun ratio.
  capacidad?: EffectiveCapacidad;
  // Carga como revisor: 2da barra. total = todas donde es Autorizado Interno;
  // revisadas = con fecha de autorización (verde). Solo en vista por responsable.
  revisor?: { revisadas: number; total: number };
}

export function KanbanColumn({ groupId, groupName, groupColor, tasks, capacidad, revisor }: Props) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-${groupId}`,
    data: { type: 'column', groupId }
  });

  const taskIds = tasks.map(t => `task-${t.idTareaArq}`);
  const headerText = contrastingText(groupColor);

  return (
    <div
      className={`flex flex-col bg-stone-100 rounded-md min-w-[260px] max-w-[280px] flex-shrink-0
        ${isOver ? 'ring-2 ring-brand-green-100 ring-offset-1' : ''}`}
    >
      <div
        className="flex flex-col gap-1.5 px-3 py-2.5 rounded-t-md"
        style={{ background: groupColor, color: headerText }}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">{groupName}</span>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium border"
            style={{ borderColor: headerText, color: headerText }}
          >
            {tasks.length}
          </span>
        </div>
        {capacidad && capacidad.horasEfectivas > 0 && (
          <CapacidadBar
            asignadas={capacidad.horasAsignadas}
            disponibles={capacidad.horasDisponibles}
            tieneOverride={capacidad.tieneOverride}
            headerText={headerText}
          />
        )}
        {revisor && revisor.total > 0 && (
          <RevisorBar revisadas={revisor.revisadas} total={revisor.total} headerText={headerText} />
        )}
      </div>

      <div
        ref={setNodeRef}
        className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-[200px]"
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map(t => <TaskCard key={t.idTareaArq} task={t} />)}
          {tasks.length === 0 && (
            <div className="text-xs text-stone-400 text-center py-8 italic">
              Soltá una tarea aquí
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}

// Barra de capacidad: linea fina con fill proporcional + texto "X / Y h".
// Color del fill: verde-100 normal, amber 80%+, rojo 100%+. El fill usa
// rgba con headerText (texto contraste del header) para que se vea bien
// sobre cualquier color de columna.
function CapacidadBar({
  asignadas, disponibles, tieneOverride, headerText,
}: {
  asignadas: number;
  disponibles: number;   // capacidad neta para tareas (bruta − revisión)
  tieneOverride: boolean;
  headerText: string;
}) {
  const ratio = disponibles > 0 ? asignadas / disponibles : (asignadas > 0 ? 1 : 0);
  const ratioClamped = Math.min(ratio, 1);
  const isOver = ratio >= 1;
  const isWarn = ratio >= 0.8 && !isOver;

  const fillColor = isOver
    ? '#BB4A4A'   // brand-red-200
    : isWarn
      ? '#F0C802' // brand-yellow
      : '#ADD010'; // brand-green-100

  return (
    <div className="flex items-center gap-2 text-[10px]" style={{ color: headerText }}>
      <div
        className="flex-1 h-1.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.25)' }}
        title={`${asignadas.toFixed(1)}h asignadas de ${disponibles.toFixed(1)}h disponibles${tieneOverride ? ' (override)' : ''}`}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${ratioClamped * 100}%`, background: fillColor }}
        />
      </div>
      <span className="font-mono whitespace-nowrap">
        {asignadas.toFixed(1)}/{disponibles.toFixed(0)}h
        {tieneOverride && <span title="Override de sprint" className="ml-0.5">*</span>}
      </span>
    </div>
  );
}

// Barra de revisión: progreso de revisadas (con fecha de autorización) sobre
// el total de tareas donde es Autorizado Interno. Verde = revisadas; el vacío
// = pendientes de revisar.
function RevisorBar({
  revisadas, total, headerText,
}: {
  revisadas: number;
  total: number;
  headerText: string;
}) {
  const ratio = total > 0 ? revisadas / total : 0;
  return (
    <div className="flex items-center gap-2 text-[10px]" style={{ color: headerText }}>
      <Eye size={11} weight="bold" className="shrink-0 opacity-80" />
      <div
        className="flex-1 h-1.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.25)' }}
        title={`${revisadas} revisadas de ${total} como revisor`}
      >
        <div className="h-full transition-all" style={{ width: `${ratio * 100}%`, background: '#1D9E75' }} />
      </div>
      <span className="font-mono whitespace-nowrap">{revisadas}/{total} rev</span>
    </div>
  );
}